import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC, MouseEvent } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import Button from '@/components/Button';
import { FormLayout, Input, Label, Larger, Textarea } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import { FBImageDistributionResource } from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

import { CommentHolder, CommentNote } from './PostModal';

export interface FormDataT {
  name: string;
  link: string;
  owner: string;
  message: string;
  note: string;
  post_date: string;
}

const StyledButton = styled(Button)`
  padding: 0 6px;
`;

const Img = styled.img`
  width: 100%;
  height: auto;
`;

interface Props {
  onClose: () => void;
  post: FBImageDistributionResource;
}

const ScheduledPostEditForm: FC<Props> = ({ onClose, post }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const isDateEditable =
    DateTime.fromISO(post.postDate!) >= DateTime.now().plus({ minutes: 10 });

  const {
    handleSubmit,
    clearErrors,
    control,
    setError,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      name: post.name ?? '',
      message: post.message ?? '',
      link: post.link ?? '',
      owner: post.owner ?? '',
      post_date: post.postDate ?? '',
    },
  });

  const onSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    clearErrors();
    return handleSubmit(async ({ post_date, ...data }) => {
      try {
        await api.put<FormDataT, { id: number }>(
          `/v1/distribution/image-posts/${post.id}/update-posted`,
          !isDateEditable ? data : { ...data, post_date },
        );

        await queryClient.invalidateQueries(['fb_image_distribution'], {
          exact: false,
        });

        alerts.success('A post has been updated successfully');

        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel heading="Edit schedule" onClose={onClose}>
        <SidePanelBody autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField
                label="Post Name"
                control={control}
                name="name"
                placeholder="Add name"
              />
              <Larger>
                <Label>Image</Label>
                <Img src={post.image.versions.original} loading="lazy" />
              </Larger>
              <FormField label="Article Link" control={control} name="link" />
              <FormField label="Owner" control={control} name="owner" />
              <Larger>
                <CommentHolder>
                  <FormField
                    label="Share Message"
                    control={control}
                    name="message"
                    type="textarea"
                  />
                  <CommentNote $color="gray" $type="smallBold">
                    <strong>Note:</strong> If first comment isn't activated,
                    article link will be automatically pasted from Article Link
                    field.
                  </CommentNote>
                </CommentHolder>
              </Larger>
              <Larger>
                <CommentHolder>
                  <Label>Comment Message</Label>
                  <Textarea value={post.comment ?? ''} disabled />
                  <CommentNote $color="gray" $type="smallBold">
                    <strong>Note:</strong> Article link will be automatically
                    pasted from Article Link field.
                  </CommentNote>
                </CommentHolder>
              </Larger>
              <Larger>
                <Label>Facebook Page</Label>
                <Input disabled value={post.channel.name} />
              </Larger>
              <FormField<FormDataT>
                label="Date & Time"
                control={control}
                name={`post_date`}
                type="datepicker"
                disabled={!isDateEditable}
              />
              <FormField
                label="Note (Internal Information)"
                control={control}
                name="note"
                type="textarea"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
              onClick={onSubmit}
            >
              Update
            </Button>
            <StyledButton color="secondary" type="button" onClick={onClose}>
              Cancel
            </StyledButton>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default ScheduledPostEditForm;
